// Device liveness + seed status — a direct port of the Flutter helpers in
// homeScreenFunctions.dart (getStatus / getStatusCached / getInitialUpdate).
//
// WHY THE CACHE EXISTS (same reasoning as Flutter):
// the badge is ONE HTTP call PER DEVICE, and every re-render would otherwise
// re-fire all of them — a request storm on each scroll/state change. So:
//   • getStatusCached() hands back the SAME promise for a short TTL
//   • each request has a hard timeout — a badge is never worth hanging a card on
//   • anything unknown/failed/offline resolves to the blue marker
//
// Badge values:
//   '🟢'          heartbeat within ONLINE_WINDOW_MS
//   '🔴 <mins>'   last seen N minutes ago
//   '🔵'          unknown (offline, timeout, or error)
//
// The window is NOT Flutter's 60 seconds — see ONLINE_WINDOW_MS for why that
// number only ever worked by accident.

import { getDeviceStatus, getDeviceDetailFresh } from '../api/api';
import { isOffline } from '../offline/offlineMode';
import { confirmLive, STALE_ASK_MS } from './liveness';
import { lastHeardAt } from '../mqtt/mqttClient';

export const STATUS_TTL_MS = 45000;
export const STATUS_TIMEOUT_MS = 6000;

export const ONLINE = '🟢';
export const UNKNOWN = '🔵';
/** No badge at all — for a device that has no heartbeat to judge (below). */
export const NO_HEARTBEAT = '';

/**
 * ⚠️ A BLE LAMP GROUP HAS NO HEARTBEAT, SO "OFFLINE" MEANS NOTHING FOR IT.
 *
 * Parents and groups of BLE lamps (DeviceID 100, unique numbers "BL-L…") are
 * DevicesInstalled rows with no ESP behind them: nothing ever stamps their
 * LastUpdatedDateTime, so they read "🔴 906" — minutes since they were CREATED
 * — and "Hide offline" removed them from the room (seen 2026-10-11, Tt1 and
 * Trial 1). They are reached over the phone's Bluetooth, not the server, so
 * there is no online/offline to show: no badge, no dot, never hidden. Their
 * own screen says when they were last used instead.
 *
 * Takes a device row or a bare unique number.
 */
export function hasNoHeartbeat(deviceOrUno) {
  if (deviceOrUno && typeof deviceOrUno === 'object') {
    if (Number(deviceOrUno.DeviceID ?? deviceOrUno.DeviceId) === 100) return true;
    return hasNoHeartbeat(deviceOrUno.UniqueNumber ?? deviceOrUno.uniqueNumber);
  }
  return /^BLM?-/i.test(String(deviceOrUno || ''));
}

const cache = new Map(); // uno -> { promise, at }

// ── Two facts from the source, not from guesswork ──────────────────────────
//
// 1. THE HEARTBEAT IS EVERY THREE MINUTES.
//    OTAOneSwitchwithButton.ino, the ESP8266 switch firmware:
//
//        if (ServerConnected && (millis() > LastIpUpdatedTime + 180000)) {
//            ProcessToServer("UpdateDeviceNetworkDetailsNew", 986);
//            ...
//        }
//
//    which the backend turns into
//    UpdateDateTimeInDevicesInstalledNew -> DevicesInstalled.LastUpdatedDateTime.
//    Note the `ServerConnected` guard: a device that loses the server STOPS
//    stamping immediately, which is exactly the signal we want.
//
// 2. THE STAMP IS IST, LABELLED UTC. functions.js, the same backend:
//
//        GetDateTimeStamp: function () {
//          return moment().utcOffset("+05:30").format("YYYY-MM-DD HH:mm:ss");
//        }
//
//    It writes IST wall-clock as a naive string; MySQL stores it zoneless and
//    the JSON serialiser appends a `Z`. So every row parses 5½ hours in the
//    FUTURE. That is by construction and fixed — not a clock that might drift.
//
// ⚠️ IF THE SERVER IS EVER FIXED TO WRITE REAL UTC, set SERVER_TZ_OFFSET_MS to
// 0. Leaving it would make every device read 5½ hours stale, i.e. permanently
// offline. One line, and it is the only line.
export const SERVER_TZ_OFFSET_MS = 5.5 * 60 * 60 * 1000;

// How stale a heartbeat may be before a device counts as offline.
//
// ⚠️ NOT 60 SECONDS. The Flutter original used 60s, but it also compared
// against the skewed stamp above, so the subtraction came out NEGATIVE and
// every device passed — the threshold was never actually exercised. Porting the
// number without the bug made green unreachable: a healthy device is on average
// 90 seconds stale and routinely three minutes.
//
// ⚠️ TWENTY, AND TEN WAS NOT MERELY TIGHT — IT WAS INSIDE THE NOISE.
//
// `LastUpdatedDateTime` is not a heartbeat. The server refreshes it in a BATCH,
// and the batch period is about the same as the old window, so a healthy device
// spends its whole life drifting between roughly 5 and 10 minutes stale and then
// snapping back. Nothing is ever fresher than five minutes.
//
// Measured across the whole estate (203 devices, UserId 1, 2026-08-09), age of
// every device carrying a heartbeat:
//
//     0–5 min      0 devices        <- nothing is EVER this fresh
//     5–8 min     27
//     8–10 min     3                <- these are 30 seconds from vanishing
//    10–60 min     0                <- a completely empty band
//    60+ min      20   +  153 stale for more than a day
//
// So a 10-minute cut fell right where the live devices sit: three of the thirty
// working devices were already within two minutes of being hidden, and when the
// batch ran late EVERY device crossed at once — which is exactly the reported
// "turn on Hide offline and they all disappear, even the ones that are online".
//
// The data also hands us the answer: between 10 and 60 minutes there is NOTHING.
// The live cluster and the dead one are separated by a 50-minute gap, so any
// threshold in it classifies perfectly. Twenty is double the worst live reading
// and still a third of the way to the nearest genuinely-offline device, which
// leaves room for one missed batch without touching the dead band.
//
// The cost is the other direction — a device that dies is still shown as online
// for up to twenty minutes. That is the right way round: the app is a remote
// control, and briefly stale beats crying wolf.
export const ONLINE_WINDOW_MS = 20 * 60 * 1000;

// Kept as a no-op so callers that used the old learned-offset mechanism do not
// break; the offset is now known from the server's source and needs no learning.
export function noteServerTimestamp() {}

export const getServerSkewMs = () => SERVER_TZ_OFFSET_MS;

// How long ago a server timestamp really was, in ms.
//
// Negative values clamp to 0: with the offset applied, "the future" only means
// the row landed between our two clock readings.
export function ageOf(ms) {
  const age = Date.now() + SERVER_TZ_OFFSET_MS - ms;
  return age < 0 ? 0 : age;
}

/** Whether a server timestamp is recent enough to call the device online. */
export const isRecent = (ms) => ageOf(ms) < ONLINE_WINDOW_MS;

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

// Uncached liveness lookup — prefer getStatusCached() from components.
export async function fetchLiveness(uniqueNumber) {
  // Offline: the server is unreachable, so the live status is unknown by
  // definition. Return blue IMMEDIATELY rather than firing a request that will
  // time out — this is per device, so a room of 10 devices otherwise sits for
  // 6 seconds doing nothing before showing the same answer.
  if (isOffline()) return UNKNOWN;
  try {
    const res = await withTimeout(getDeviceStatus(uniqueNumber), STATUS_TIMEOUT_MS);
    const rows = Array.isArray(res.data) ? res.data : [];
    const last = rows[0] && rows[0].LastUpdatedDateTime;
    if (!last) return UNKNOWN;
    const stamp = new Date(last).getTime();
    if (!Number.isFinite(stamp)) return UNKNOWN;
    const diffMs = ageOf(stamp);
    // ⚠️ "Heard recently" is not "will take a command" — the HTTP network ping keeps
    // this fresh with MQTT dead (2026-10-17). So a device that would be 🟢 is ASKED
    // (liveness.confirmLive, cached 3 min); no answer = 🔴 with the minutes since
    // its last MQTT message (LastMqttPing — the details page's "Last seen").
    if (diffMs < ONLINE_WINDOW_MS) {
      if ((await confirmLive(uniqueNumber)) === false) {
        const contact = await getLastContactAge(uniqueNumber);
        return `🔴 ${Math.floor((contact != null ? contact : diffMs) / 60000)}`;
      }
      return ONLINE;
    }
    // ⚠️ A STALE STAMP IS ASKED TOO (2026-10-19): current sensors (16, 82)
    // seldom refresh it — 801038 read "🔴 107" while posting every 30 s and
    // answering MQTT. Within a week of the stamp, the device decides.
    if (diffMs < STALE_ASK_MS && (await confirmLive(uniqueNumber)) === true) return ONLINE;
    return `🔴 ${Math.floor(diffMs / 60000)}`;
  } catch (e) {
    return UNKNOWN;
  }
}

// Cached liveness — call THIS from components. Returns the same promise within
// the TTL so re-renders reuse it instead of refetching.
export function getStatusCached(uniqueNumber) {
  if (hasNoHeartbeat(uniqueNumber)) return Promise.resolve(NO_HEARTBEAT);
  const key = String(uniqueNumber);
  const now = Date.now();
  const hit = cache.get(key);
  if (hit && now - hit.at < STATUS_TTL_MS) return hit.promise;
  const promise = fetchLiveness(uniqueNumber);
  cache.set(key, { promise, at: now });
  return promise;
}

// Drop cached statuses so the next render refetches — wire to pull-to-refresh.
export function clearStatusCache() {
  cache.clear();
}

// ── Last-seen age, for callers that draw their own indicator ───────────────
// Same request and the same TTL discipline as getStatusCached, but returns the
// corrected AGE in ms rather than a badge string, so a component can decide its
// own thresholds and wording.
//
// Exists because GetListofDevicesbyUserId returns no timestamp at all: a tile
// fed from that list has nothing on the row to work from, and asking the status
// endpoint is the only way to show anything.
const ageCache = new Map(); // uno -> { promise, at }

export async function fetchLastSeenAge(uniqueNumber) {
  if (isOffline()) return null;
  try {
    const res = await withTimeout(getDeviceStatus(uniqueNumber), STATUS_TIMEOUT_MS);
    const rows = Array.isArray(res.data) ? res.data : [];
    const last = rows[0] && rows[0].LastUpdatedDateTime;
    if (!last) return null;
    const stamp = new Date(last).getTime();
    return Number.isFinite(stamp) ? ageOf(stamp) : null;
  } catch (e) {
    return null;
  }
}

export function getLastSeenCached(uniqueNumber) {
  if (hasNoHeartbeat(uniqueNumber)) return Promise.resolve(null);
  const key = String(uniqueNumber);
  const now = Date.now();
  const hit = ageCache.get(key);
  if (hit && now - hit.at < STATUS_TTL_MS) return hit.promise;
  const promise = fetchLastSeenAge(uniqueNumber);
  ageCache.set(key, { promise, at: now });
  return promise;
}

// ── How long since we last had ANY contact with a device ────────────────────
// For the red "N min" after a device fails a live check (liveness.confirmLive):
// minutes since its last MQTT CONTACT — the newest of a message this app heard
// (mqttClient.lastHeardAt, our clock) and its last status report (LastMqttPing /
// LastStatusTime). The server half is fetched only for a device that did NOT answer.
//
// ⚠️ NEVER THE HTTP NETWORK PING (2026-10-21). NetworkLastUpdatedDateTime /
// LastUpdatedDateTime are refreshed every 1–3 min by OtaForTuya / the switch
// firmware over HTTP whatever MQTT is doing, so counting from them made every red
// dot read "0 min" or "1 min" — "ac" and "WASHROOM" in Sanjay Bedroom. That ping
// is still worth knowing (powered, on the internet), so it is returned apart as
// `pingAge` for the tooltip, never as the offline time.
const contactCache = new Map(); // uno -> { promise, at }
function serverContactAge(key) {
  const hit = contactCache.get(key);
  if (hit && Date.now() - hit.at < STATUS_TTL_MS) return hit.promise;
  const promise = getDeviceDetailFresh(key)
    .then((res) => {
      const row = Array.isArray(res?.data) ? res.data[0] : null;
      const ageCols = (cols) => cols
        .map((k) => (row && row[k] ? new Date(row[k]).getTime() : NaN))
        .filter((t) => Number.isFinite(t) && t > Date.UTC(2000, 0, 1))   // 1901 = never
        .map((t) => ageOf(t));
      const mqtt = ageCols(['LastMqttPing', 'LastStatusTime']);
      const ping = ageCols(['NetworkLastUpdatedDateTime', 'LastUpdatedDateTime']);
      return {
        mqtt: mqtt.length ? Math.min(...mqtt) : null,
        ping: ping.length ? Math.min(...ping) : null,
      };
    })
    .catch(() => null);
  contactCache.set(key, { promise, at: Date.now() });
  return promise;
}

/**
 * -> { mqtt, ping } in ms (either may be null). `mqtt` = since the last MQTT
 * contact (what "offline for N min" means); `ping` = since the HTTP network
 * ping (powered + internet), for the tooltip only.
 */
export async function getContactAges(uniqueNumber) {
  const key = String(uniqueNumber || '').trim();
  if (!key) return { mqtt: null, ping: null };
  const heard = lastHeardAt(key);
  const heardAge = heard ? Date.now() - heard : null;
  const server = (await serverContactAge(key)) || { mqtt: null, ping: null };
  const mqtt = [heardAge, server.mqtt].filter((a) => a != null);
  return { mqtt: mqtt.length ? Math.min(...mqtt) : null, ping: server.ping };
}

/** ms since the last MQTT contact, or null when nothing is known. */
export async function getLastContactAge(uniqueNumber) {
  return (await getContactAges(uniqueNumber)).mqtt;
}
// The old name, kept for callers written against it.
export const getMqttAgeCached = getLastContactAge;

// Seed a control screen's state from the last status the server recorded.
// Flutter's getInitialUpdate(): take Status, append '#', then keep the substring
// between ']' and '#'. Returns '' on any failure — control screens treat '' as
// "state unknown → show blue and wait for MQTT". Never return a placeholder
// word here: a non-empty 'Unknown' used to poison the seed parsers.
export async function fetchSeedStatus(uniqueNumber) {
  // Offline: no cached live status exists (status arrives over MQTT), so seed
  // empty at once instead of delaying the control screen behind a doomed
  // request. The screen shows "unknown" and updates when a status arrives.
  if (isOffline()) return '';
  try {
    const res = await withTimeout(getDeviceStatus(uniqueNumber), STATUS_TIMEOUT_MS);
    const rows = Array.isArray(res.data) ? res.data : [];
    const raw = rows[0] && rows[0].Status;
    if (raw == null) return '';
    const s = `${raw}#`;
    return s.substring(s.indexOf(']') + 1, s.indexOf('#'));
  } catch (e) {
    return '';
  }
}
